import React from 'react';
import CalendarDays from 'lucide-react/dist/esm/icons/calendar-days.js';
import { parseDrawDate, getDrawIanaTimeZone } from '../utils/dateFormat';
import { useTranslations } from '../contexts/TranslationContext';
import { useLanguage } from '../contexts/LanguageContext';

export default function DrawDateBadge({
  draw,
  date,
  label,
  className = '',
  compact = false,
  variant = 'glass',
  locale,
}) {
  const { t } = useTranslations();
  const { language } = useLanguage();
  const gameNumber = draw?.game_number ?? draw?.gameNumber ?? draw?.current_game_number ?? null;
  const weekNumber = draw?.week_number ?? draw?.weekNumber ?? draw?.current_week_number ?? null;
  const isCtaGame = (draw?.game_type === 'chaseTheAce' || draw?.gameType === 'chaseTheAce') && gameNumber != null;

  const resolvedLabel = label ?? (isCtaGame
    ? `${t('chaseTheAce.game', 'GAME')} ${gameNumber} · ${t('cinematic.draw', 'DRAW')} ${weekNumber ?? draw?.draw_number ?? draw?.drawNumber ?? ''}`.trim()
    : (t('chaseTheAce.drawDate') ?? 'Draw Date'));
  const drawNum = isCtaGame ? null : (draw?.draw_number ?? draw?.drawNumber ?? null);
  const source = date ?? draw;
  const parsed = parseDrawDate(source);
  if (!parsed) return null;

  const ianaTz = getDrawIanaTimeZone(draw) || getDrawIanaTimeZone(date);
  const resolvedLocale = locale || language || undefined;
  const dateOpts = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const timeOpts = {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  };
  if (ianaTz) {
    dateOpts.timeZone = ianaTz;
    timeOpts.timeZone = ianaTz;
  }
  const formattedDate = parsed.toLocaleDateString(resolvedLocale, dateOpts);
  const formattedTime = parsed.toLocaleTimeString(resolvedLocale, timeOpts);
  const display = `${formattedDate} @ ${formattedTime}`;

  const isLight = variant === 'light';
  const badgeClass = isLight
    ? 'border-gray-200 bg-white text-gray-900 shadow-lg dark:border-white/20 dark:bg-white/10 dark:text-white'
    : 'border-white/35 bg-white/20 text-white shadow-xl shadow-black/10';
  const iconClass = isLight
    ? 'bg-gray-100 text-gray-700 dark:bg-white/15 dark:text-white'
    : 'bg-white/20 text-white';
  const labelClass = isLight
    ? 'text-gray-500 dark:text-white/65'
    : 'text-white/70';

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 backdrop-blur-md ${badgeClass} ${compact ? 'text-xs' : 'text-sm sm:text-base'} ${className}`}
      aria-label={`${resolvedLabel}${drawNum != null ? ` - #${drawNum}` : ''}: ${display}`}
    >
      <span className={`flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="flex flex-col leading-tight text-left">
        <span className={`text-[0.65rem] font-bold uppercase tracking-[0.18em] ${labelClass}`}>
          {resolvedLabel}
          {drawNum != null && (
            <span className="text-xs font-extrabold normal-case tracking-normal"> - #{drawNum}</span>
          )}
        </span>
        <span className="font-semibold">
          {display}
        </span>
      </span>
    </div>
  );
}
